import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import SectionTitle from '../components/SectionTitle';
import PostalResultRow from '../components/PostalResultRow';
import ReverseSearchHelp from '../components/ReverseSearchHelp';
import { getReverse } from '../services/api';
import { t } from '../i18n';
import { pushEvent } from '../utils/analytics';

const ReverseSearchPage = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [submitted, setSubmitted] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    setError(false);
    setResults(null);
    pushEvent('reverse_search', { query: q });
    try {
      const res = await getReverse(q);
      const rows = res.results || [];
      setResults(rows);
      setTotal(res.total ?? rows.length);
      setTruncated(!!res.truncated);
      setSubmitted(q);
      pushEvent('result_count', { page: 'reverse', count: res.total ?? rows.length });
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <SectionTitle emoji="🔍">{t('Reverse Search', '逆引き検索')}</SectionTitle>

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap', mb: 1 }}>
        <TextField
          color="secondary"
          variant="standard"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          label={t('Place name', '地名')}
          placeholder={t('e.g. Chiyoda, 千代田, or チヨダ', '例: 千代田、チヨダ、または Chiyoda')}
          sx={{ width: { xs: '100%', sm: 320 } }}
        />
        <Button type="submit" variant="contained" color="secondary" startIcon={<Search />} sx={{ px: 4 }}>
          {t('Search', '検索')}
        </Button>
      </Box>

      <ReverseSearchHelp />

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
          <CircularProgress size={36} />
          <Typography sx={{ ml: 2 }}>{t('Searching…', '検索中…')}</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {t('Something went wrong. Please try again later.', 'エラーが発生しました。しばらくしてからもう一度お試しください。')}
        </Alert>
      )}

      {results && !loading && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" sx={{ mb: 2 }} color="text.secondary">
            {t(
              `${total} ${total === 1 ? 'result' : 'results'} for “${submitted}”`,
              `「${submitted}」の検索結果 ${total} 件`
            )}
          </Typography>

          {truncated && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {t(
                `Showing the first ${results.length} of ${total}. Refine your search with a more specific place name.`,
                `${total} 件のうち最初の ${results.length} 件を表示しています。より具体的な地名で絞り込んでください。`
              )}
            </Alert>
          )}

          {results.length === 0 ? (
            <Alert severity="info">
              {t('No matching addresses found.', '一致する住所が見つかりませんでした。')}
            </Alert>
          ) : (
            <Paper
              elevation={0}
              sx={{ bgcolor: 'background.default', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}
            >
              {results.map((entry, i) => (
                <React.Fragment key={`${entry.postal_code}-${i}`}>
                  {i > 0 && <Divider />}
                  <PostalResultRow entry={entry} />
                </React.Fragment>
              ))}
            </Paper>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ReverseSearchPage;
